import { defineStore } from 'pinia';
import demonessConfig from '@/assets/branding/themes/DemonesEngineer/theme.json';
import princessConfig from '@/assets/branding/themes/SleepyPrincess/theme.json';
import networkLogoUrl from '@/assets/branding/logos/cavicode-network.png';
import demonessIdle from '@/assets/branding/themes/DemonesEngineer/idle.png';
import demonessHappy from '@/assets/branding/themes/DemonesEngineer/happy.png';
import demonessConnect from '@/assets/branding/themes/DemonesEngineer/connect.png';
import demonessWorking from '@/assets/branding/themes/DemonesEngineer/working.png';
import demonessSearch from '@/assets/branding/themes/DemonesEngineer/search.png';
import demonessShock from '@/assets/branding/themes/DemonesEngineer/shock.png';
import demonessCry from '@/assets/branding/themes/DemonesEngineer/cry.png';
import demonessProblem from '@/assets/branding/themes/DemonesEngineer/problem.png';
import princessIdle from '@/assets/branding/themes/SleepyPrincess/idle.png';
import princessWorking from '@/assets/branding/themes/SleepyPrincess/working.png';
import princessShock from '@/assets/branding/themes/SleepyPrincess/shock.png';

export const brandLogoUrl = networkLogoUrl;

export const ThemeKeys = ['DemonesEngineer', 'SleepyPrincess'] as const;
export type ThemeKey = (typeof ThemeKeys)[number];
export type ThemeMode = ThemeKey;
export type MascotState =
    | 'idle'
    | 'happy'
    | 'connect'
    | 'working'
    | 'search'
    | 'shock'
    | 'cry'
    | 'problem';

export interface BrandThemeConfig {
    name: string;
    nameKey: string;
    mode: 'Light' | 'Dark';
    accent: {
        gradientStart: string;
        gradientEnd: string;
        primary: string;
    };
    background: {
        glyphs: string[];
        color: string;
        sizeMin: number;
        sizeMax: number;
        alphas: number[];
    };
}

export interface BrandTheme {
    key: ThemeKey;
    config: BrandThemeConfig;
    mascots: Record<MascotState, string>;
}

export const brandThemes: Record<ThemeKey, BrandTheme> = {
    DemonesEngineer: {
        key: 'DemonesEngineer',
        config: demonessConfig as BrandThemeConfig,
        mascots: {
            idle: demonessIdle,
            happy: demonessHappy,
            connect: demonessConnect,
            working: demonessWorking,
            search: demonessSearch,
            shock: demonessShock,
            cry: demonessCry,
            problem: demonessProblem
        }
    },
    SleepyPrincess: {
        key: 'SleepyPrincess',
        config: princessConfig as BrandThemeConfig,
        mascots: {
            idle: princessIdle,
            happy: princessIdle,
            connect: princessIdle,
            working: princessWorking,
            search: princessIdle,
            shock: princessShock,
            cry: princessIdle,
            problem: princessIdle
        }
    }
};

const STORAGE_KEY = 'cavicode-network.theme';

export const useThemeStore = defineStore('theme', {
    state: () => ({
        mode: loadStored()
    }),
    getters: {
        current(state): BrandTheme {
            return brandThemes[state.mode];
        },
        currentConfig(state): BrandThemeConfig {
            return brandThemes[state.mode].config;
        },
        currentMascots(state): Record<MascotState, string> {
            return brandThemes[state.mode].mascots;
        },
        effective(state): 'light' | 'dark' {
            return brandThemes[state.mode].config.mode === 'Dark' ? 'dark' : 'light';
        },
        nextTheme(state): ThemeKey {
            return state.mode === 'DemonesEngineer' ? 'SleepyPrincess' : 'DemonesEngineer';
        }
    },
    actions: {
        apply() {
            if (typeof document === 'undefined') return;

            const theme = brandThemes[this.mode];
            const { accent } = theme.config;
            const root = document.documentElement;

            root.dataset.theme = this.effective;
            root.dataset.themeKey = this.mode;
            root.style.colorScheme = this.effective;
            root.style.setProperty('--accent-start', accent.gradientStart);
            root.style.setProperty('--accent-end', accent.gradientEnd);
            root.style.setProperty('--accent', accent.primary);
            root.style.setProperty('--accent-tint', hexToRgba(accent.primary, this.effective === 'dark' ? 0.16 : 0.1));
            root.style.setProperty('--accent-ring', hexToRgba(accent.primary, this.effective === 'dark' ? 0.3 : 0.18));
            root.style.setProperty('--accent-gradient', `linear-gradient(135deg, ${accent.gradientStart} 0%, ${accent.gradientEnd} 100%)`);
            root.style.setProperty('--mascot-shadow', `drop-shadow(0 24px 48px ${hexToRgba(accent.gradientEnd, 0.45)})`);
        },
        set(mode: ThemeMode) {
            if (!brandThemes[mode]) return;
            this.mode = mode;
            try {
                localStorage.setItem(STORAGE_KEY, mode);
            } catch {
                // localStorage может быть недоступен в private-mode; тема останется до перезагрузки.
            }
            this.apply();
        },
        toggle() {
            this.set(this.nextTheme);
        },
        bootstrap() {
            this.apply();
        }
    }
});

function loadStored(): ThemeKey {
    if (typeof localStorage === 'undefined') return preferredDefaultTheme();
    const v = localStorage.getItem(STORAGE_KEY);
    if (isThemeKey(v)) return v;
    if (v === 'light') return 'DemonesEngineer';
    if (v === 'dark') return 'SleepyPrincess';
    if (v === 'system') return preferredDefaultTheme();
    return preferredDefaultTheme();
}

function preferredDefaultTheme(): ThemeKey {
    if (typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'SleepyPrincess';
    }
    return 'DemonesEngineer';
}

function isThemeKey(value: string | null): value is ThemeKey {
    return value === 'DemonesEngineer' || value === 'SleepyPrincess';
}

function hexToRgba(hex: string, alpha: number): string {
    const m = hex.replace('#', '');
    const r = parseInt(m.slice(0, 2), 16);
    const g = parseInt(m.slice(2, 4), 16);
    const b = parseInt(m.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}